import { AuthorDto } from '@/Domain/api/Conversation/dto/Autor.dto';
import { InsightDto } from '@/Domain/api/Conversation/dto/Insight.dto';
import { TranslationLanguagesEnum } from '@/Domain/enum/TranslatedLanguage.enum';

export type MessageFeedbackValue = 'positive' | 'negative';

export type MessageFeedback = {
  value: MessageFeedbackValue;
  comment: string | null;
  timestamp: string;
  author: {
    id: number;
    email: string | null;
    first_name?: string | null;
    last_name?: string | null;
    avatar_url?: string | null;
  };
};

export type MessageFeedbackInput = {
  value: MessageFeedbackValue;
  comment: string | null;
};

export const COMPONENT_TYPE_PRODUCT_CAROUSEL = 'product_carousel' as const;
export const COMPONENT_TYPE_CHECKOUT_CART = 'checkout_cart' as const;
export const COMPONENT_TYPE_CALENDAR_EVENT = 'calendar_event' as const;
export const COMPONENT_TYPE_CTA_BUTTON = 'cta_button' as const;

export type AttachmentType =
  | 'image'
  | 'file'
  | 'audio'
  | typeof COMPONENT_TYPE_PRODUCT_CAROUSEL
  | typeof COMPONENT_TYPE_CHECKOUT_CART
  | typeof COMPONENT_TYPE_CALENDAR_EVENT
  | typeof COMPONENT_TYPE_CTA_BUTTON;

export type MessageAttachment = {
  attachment_type: AttachmentType;
  url: string;
  filename: string | null;
  mime_type: string | null;
  duration_seconds: number | null;
  data?: Record<string, unknown> | null;
};

export type ProductCarouselProduct = {
  product_name: string;
  image_url: string;
  url: string;
  price: string;
  description?: string | null;
};

export type ProductCarouselData = {
  component_type: typeof COMPONENT_TYPE_PRODUCT_CAROUSEL;
  products: ProductCarouselProduct[];
};

export type CartLineItemProduct = {
  title: string;
  variant_title: string | null;
  quantity: number;
  unit_price: string;
};

export type CheckoutCartData = {
  component_type: typeof COMPONENT_TYPE_CHECKOUT_CART;
  checkout_url: string;
  checkout_label: string;
  /** TODO(temp-demo): PayPo flag is temporary for a demo — remove after demo. */
  is_paypo: boolean;
  currency: string;
  total_amount: string;
  line_items: CartLineItemProduct[];
};

export type CalendarEventData = {
  component_type: typeof COMPONENT_TYPE_CALENDAR_EVENT;
  title: string;
  start_iso: string;
  end_iso: string | null;
  timezone_label: string | null;
  location: string | null;
  meeting_url: string | null;
  host_name: string | null;
  attendee_name: string | null;
  attendee_email: string | null;
};

export type CtaButtonData = {
  component_type: typeof COMPONENT_TYPE_CTA_BUTTON;
  label: string;
  url: string;
  /** Lead-in sentence rendered above the button, for a CTA the reply text does not introduce itself. */
  description?: string;
  /** Allowlisted icon name; anything unrecognised renders as a plain button. */
  icon?: string;
};

export type MessageToolCallDto = {
  tool_name: string;
  tool_input: Record<string, unknown>;
  tool_output_preview: string;
  status: 'success' | 'error';
  http_status_code: number | null;
  duration_ms: number | null;
};

export type DeliveryStatus = 'sent' | 'delivered' | 'read' | 'failed';

export type Message = {
  id: string;
  content: string;
  content_inbox: string;
  content_translated: string | null;
  created_at: string;
  author: AuthorDto;
  message_insights: InsightDto[];
  feedback: MessageFeedback | null;
  is_welcome: boolean;
  sources_generated: boolean;
  translated_language: TranslationLanguagesEnum;
  attachments?: MessageAttachment[];
  tool_calls?: MessageToolCallDto[];
  delivery_status?: DeliveryStatus | null;
  delivery_error?: string | null;
};
